import { useRef, useState } from 'react';
import './SiteUnitCommandsPopup.scoped.scss';
import Button from '@components/_elements/Button/Button';
import storeConnector from '@store/storeConnector';
import { isNumeric, roundToTwoNonZero } from '@utils/mathHelpers';
import useAlias from '@alias';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';

const PowerCommandForm = ({
  siteMeta,
  popupProps,
  Qset,
  Pset,
  showConfPopup,
  allowedMinReal,
  allowedMaxReal,
  allowedMinReactive,
  allowedMaxReactive,
  setOverInput,
  isSplitPQMode,
  isSecondaryPowerCmd,
}) => {
  const alias = useAlias();
  const sendSplitPCmd = () => {
    showConfPopup(
      `${siteMeta.Site_Commands_Power_Secondary?.splitPCommand}${real}`,
      'value',
    );
  };
  const sendSplitQCmd = () => {
    showConfPopup(
      `${siteMeta.Site_Commands_Power_Secondary?.splitQCommand}${reactive}`,
      'value',
    );
  };
  const sendUnifiedCmd = () => {
    showConfPopup(
      `${siteMeta.Site_Commands_Power_Secondary?.unifiedCommand || 'Power::'}${real}::${reactive}`,
      'value',
    );
  };
  const powerLabel = isSecondaryPowerCmd
    ? siteMeta.Site_Commands_Power_Secondary?.label || alias('Power')
    : alias('Power');
  const pInputLabel = isSecondaryPowerCmd
    ? siteMeta.Site_Commands_Power_Secondary?.labelP || alias('Real (P):')
    : alias('Real (P):');
  const qInputLabel = isSecondaryPowerCmd
    ? siteMeta.Site_Commands_Power_Secondary?.labelQ || alias('Reactive (Q):')
    : alias('Reactive (Q):');
  const [reactive, setReactive] = useState(+Qset || 0);
  const [real, setReal] = useState(+Pset || 0);
  const inputFirstRef = useRef();
  const inputSecondRef = useRef();

  // Function to get error class based on conditions
  const getErrorClass = (value, allowedMin, allowedMax) => {
    // Treat empty/undefined input as invalid
    if (value === '' || value == null) {
      return 'error';
    }

    const v = Number(value);
    const min = Number(allowedMin);
    const max = Number(allowedMax);

    // If limits are not valid numbers, only check that the value itself is numeric
    if (Number.isNaN(min) || Number.isNaN(max)) {
      return isNumeric(value) ? '' : 'error';
    }

    if (!Number.isFinite(v)) {
      return 'error';
    }
    return v >= min && v <= max ? '' : 'error';
  };

  return (
    <>
      <div className='popup-row power-command-form-container'>
        <div className='bold vertical-margin'>
          {popupProps.popupType === 'unit' ? alias('Unit Power') : powerLabel}
        </div>
        <div className='popup-table'>
          <div className='popup-table-col'>
            <div>{pInputLabel}</div>
            <div className='range-lable' />
            <div>{qInputLabel}</div>
            <div className='range-lable' />
          </div>
          <div className='popup-table-col'>
            <div className='split-input'>
              <div className='split-input-col'>
                <input
                  name='real-input'
                  ref={inputFirstRef}
                  className={
                    `comand-popup-inp ` +
                    getErrorClass(real, allowedMinReal, allowedMaxReal)
                  }
                  type='number'
                  step='0.1'
                  value={real}
                  onMouseEnter={() => setOverInput(true)}
                  onMouseLeave={() => setOverInput(false)}
                  onChange={(e) => setReal(e.target.value)}
                />
                <div className='range-lable'>
                  {alias('Range from')} {roundToTwoNonZero(allowedMinReal)}{' '}
                  {alias('to')} {roundToTwoNonZero(allowedMaxReal)}
                </div>
              </div>
              <div className='split-input-col'>kW</div>
              {isSplitPQMode && (
                <Button
                  className='split-input-col'
                  size='s'
                  disabled={
                    real === '' ||
                    getErrorClass(real, allowedMinReal, allowedMaxReal) ===
                      'error'
                  }
                  onClick={() => sendSplitPCmd()}
                >
                  <FormattedAlias id='Send' />
                </Button>
              )}
            </div>
            <div className='split-input'>
              <div className='split-input-col'>
                <input
                  name='reactive-input'
                  ref={inputSecondRef}
                  className={
                    `comand-popup-inp ` +
                    getErrorClass(
                      reactive,
                      allowedMinReactive,
                      allowedMaxReactive,
                    )
                  }
                  type='number'
                  step='0.1'
                  value={reactive}
                  onMouseEnter={() => setOverInput(true)}
                  onMouseLeave={() => setOverInput(false)}
                  onChange={(e) => setReactive(e.target.value)}
                />
                {!isNaN(allowedMinReactive) && (
                  <div className='range-lable'>
                    {alias('Range from')}{' '}
                    {roundToTwoNonZero(allowedMinReactive)} {alias('to')}&nbsp;
                    {roundToTwoNonZero(allowedMaxReactive)}
                  </div>
                )}
              </div>
              <div className='split-input-col'>kvar</div>
              {isSplitPQMode && (
                <Button
                  className='split-input-col'
                  size='s'
                  disabled={
                    reactive === '' ||
                    getErrorClass(
                      reactive,
                      allowedMinReactive,
                      allowedMaxReactive,
                    ) === 'error'
                  }
                  onClick={() => sendSplitQCmd()}
                >
                  <FormattedAlias id='Send' />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {getErrorClass(real, allowedMinReal, allowedMaxReal) === 'error' && (
        <div className='popup-row warn-alert'>
          <FormattedAlias
            id='The value of real power must be between {min} and {max}'
            values={{ min: allowedMinReal, max: allowedMaxReal }}
          />
        </div>
      )}
      {getErrorClass(reactive, allowedMinReactive, allowedMaxReactive) ===
        'error' && (
        <div className='popup-row warn-alert'>
          <FormattedAlias
            id='The value of reactive power must be between {min} and {max}'
            values={{
              min: roundToTwoNonZero(allowedMinReactive),
              max: roundToTwoNonZero(allowedMaxReactive),
            }}
          />
        </div>
      )}
      {!isSplitPQMode && (
        <div className='popup-row commands-popup-control'>
          <Button
            size='m'
            disabled={
              real === '' ||
              reactive === '' ||
              getErrorClass(
                reactive,
                allowedMinReactive,
                allowedMaxReactive,
              ) === 'error' ||
              getErrorClass(real, allowedMinReal, allowedMaxReal) === 'error'
            }
            onClick={() => sendUnifiedCmd()}
          >
            <FormattedAlias id='Send' />
          </Button>
        </div>
      )}
    </>
  );
};

export default storeConnector(PowerCommandForm, {
  config: ['siteMeta'],
  service: ['popupProps'],
});
